import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import InfraNav from './components/InfraNav';
import InfraFooter from './components/InfraFooter';
import InfraCTA from './components/InfraCTA';
import './infrastructure.css';

export default function InfrastructureWallets() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="infra-page" data-testid="infrastructure-wallets">
      <InfraNav transparent={false} />

      {/* ══════ HERO ══════ */}
      <div className="infra-product-hero">
        <div className="infra-product-hero-text">
          <div className="infra-product-eyebrow" style={{ color: 'var(--pebbles-green)' }}>VIRTUAL WALLETS</div>
          <h1 className="infra-product-headline">Give every entity a financial identity.</h1>
          <p className="infra-product-desc">
            Dedicated virtual accounts for companies, employees, and AI agents. Move money anywhere. Track everything in a double-entry ledger.
          </p>
          <div className="infra-product-buttons">
            <Link to="/login" className="infra-btn-primary" data-testid="wallets-get-started">Get started</Link>
            <a href="mailto:hello@pebbles.financial" className="infra-btn-secondary">Contact sales</a>
          </div>
        </div>
        <div className="infra-product-hero-mockup">
          {/* Wallet balance card mockup */}
          <div className="infra-feature-card">
            <div className="infra-feature-card-inner">
              <div className="infra-mockup-row">
                <span className="infra-mockup-label">Employee wallet</span>
                <span className="infra-badge infra-badge-green">Active</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 600, color: 'var(--pebbles-dark)', marginBottom: 4 }}>₦284,500.00</div>
              <div style={{ fontSize: 11, color: 'var(--pebbles-gray-400)', marginBottom: 16 }}>VA: 8012345678 · Assets MFB</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ flex: 1, padding: 8, textAlign: 'center', borderRadius: 6, fontSize: 11, fontWeight: 500, background: 'var(--pebbles-green)', color: '#fff' }}>Send</div>
                <div style={{ flex: 1, padding: 8, textAlign: 'center', borderRadius: 6, fontSize: 11, fontWeight: 500, background: '#fff', border: '1px solid var(--pebbles-gray-200)', color: 'var(--pebbles-gray-500)' }}>Receive</div>
                <div style={{ flex: 1, padding: 8, textAlign: 'center', borderRadius: 6, fontSize: 11, fontWeight: 500, background: '#fff', border: '1px solid var(--pebbles-gray-200)', color: 'var(--pebbles-gray-500)' }}>History</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 1: Virtual Accounts ══════ */}
      <div className="infra-feature">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(29,158,117,0.1)', color: 'var(--pebbles-green)' }}>Accounts</span>
            <h3 className="infra-feature-title">Virtual accounts for companies and employees</h3>
            <p className="infra-feature-desc">
              Every company and employee gets a dedicated virtual account number backed by our banking partners. Receive funds, hold balances, and track everything.
            </p>
            <ul className="infra-feature-bullets infra-check-green">
              <li>Company wallets with dedicated virtual accounts</li>
              <li>Employee wallets created automatically</li>
              <li>Agent wallets with spending rules</li>
              <li>Wallet status management: active, frozen, suspended</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { name: 'Company Wallet', va: '8010001234', bal: '₦12,450,000', color: 'var(--pebbles-pink)' },
                  { name: 'Employee Wallet', va: '8012345678', bal: '₦284,500', color: 'var(--pebbles-green)' },
                  { name: 'Agent Wallet', va: '8019876543', bal: '₦142,800', color: 'var(--pebbles-blue)' },
                ].map((w, i) => (
                  <div key={i} className="infra-data-row" style={{ border: '1px solid var(--pebbles-gray-100)', borderRadius: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--pebbles-dark)' }}>{w.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--pebbles-gray-400)' }}>VA: {w.va}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pebbles-dark)' }}>{w.bal}</div>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: w.color, marginLeft: 'auto' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 2: Transfers ══════ */}
      <div className="infra-feature infra-feature--reverse infra-feature--gray">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(29,158,117,0.1)', color: 'var(--pebbles-green)' }}>Transfers</span>
            <h3 className="infra-feature-title">Move money anywhere, instantly</h3>
            <p className="infra-feature-desc">
              Employee-to-bank, company-to-employee, P2P, bulk disbursements, scheduled transfers. 209 Nigerian banks supported. Bank name resolution and OTP verification on every transaction.
            </p>
            <ul className="infra-feature-bullets infra-check-green">
              <li>External payouts to any bank</li>
              <li>Internal P2P and bulk transfers</li>
              <li>Saved beneficiaries for repeat transfers</li>
              <li>Per-transaction OTP verification</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner">
                <div className="infra-stepper" style={{ marginBottom: 16 }}>
                  <div className="infra-step-box infra-step-box--active">Amount</div>
                  <span className="infra-step-arrow">&rarr;</span>
                  <div className="infra-step-box">Recipient</div>
                  <span className="infra-step-arrow">&rarr;</span>
                  <div className="infra-step-box">OTP</div>
                  <span className="infra-step-arrow">&rarr;</span>
                  <div className="infra-step-box">Done</div>
                </div>
                <div className="infra-data-row"><span className="infra-data-label">Amount</span><span className="infra-data-value">₦50,000</span></div>
                <div className="infra-data-row"><span className="infra-data-label">Recipient</span><span className="infra-data-value">Ayo Balogun</span></div>
                <div className="infra-data-row"><span className="infra-data-label">Bank</span><span className="infra-data-value">GTBank</span></div>
                <div className="infra-data-row"><span className="infra-data-label">Fee</span><span className="infra-data-value">₦25</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 3: Ledger ══════ */}
      <div className="infra-feature">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(29,158,117,0.1)', color: 'var(--pebbles-green)' }}>Ledger</span>
            <h3 className="infra-feature-title">Bank-grade record-keeping from day one</h3>
            <p className="infra-feature-desc">
              Double-entry bookkeeping on every transaction. Pessimistic locking prevents race conditions. Nightly reconciliation catches discrepancies automatically.
            </p>
            <ul className="infra-feature-bullets infra-check-green">
              <li>Every debit has a matching credit</li>
              <li>Balance-before and balance-after on every entry</li>
              <li>Nightly automated reconciliation</li>
              <li>Non-negative balance constraints at database level</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner" style={{ overflowX: 'auto' }}>
                <table className="infra-ledger">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>Mar 7</td><td>Payroll credit</td><td></td><td style={{color:'var(--pebbles-green)'}}>₦350,000</td><td>₦350,000</td></tr>
                    <tr><td>Mar 8</td><td>Food benefit (GDL+)</td><td style={{color:'#E24B4A'}}>₦30,000</td><td></td><td>₦320,000</td></tr>
                    <tr><td>Mar 8</td><td>Transport (provider)</td><td style={{color:'#E24B4A'}}>₦20,000</td><td></td><td>₦300,000</td></tr>
                    <tr><td>Mar 10</td><td>Transfer to GTBank</td><td style={{color:'#E24B4A'}}>₦50,000</td><td></td><td>₦250,000</td></tr>
                    <tr><td>Mar 12</td><td>P2P from J. Obi</td><td></td><td style={{color:'var(--pebbles-green)'}}>₦15,000</td><td>₦265,000</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <InfraCTA />
      <InfraFooter />
    </div>
  );
}
